'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["customObjects", "customObjectsLoading", "frequentlyUsedProperties", "hasPermissionsForTickets", "searchQuery", "searchQueryResults", "ticketToggleEnabled", "showSearchResults", "captchaEnabled", "captchaConfigurable", "currentLegalConsentOption", "usedFormFieldIds", "isPaymentsToggleEnabled", "hasAdConsentAccess", "hasFormsATCAccess", "setActiveTab", "updateLegalConsentOption", "updateValueOnFormDefinition", "fetchInboxesAndSetInboxId"];
import { Fragment } from 'react';
import { connect } from 'react-redux';
import { appendFormFieldGroup, updateDraggedItem } from 'FormsUI/actions/FormDragDrop';
import updateLegalConsentOptionFromAction from 'FormsUI/actions/LegalConsent/updateLegalConsentOption';
import { LoadingDots } from 'foundations-components/transitional/components/LoadingDots';
import { fetchInboxesAndSetInboxId as fetchInboxesAndSetInboxIdAction } from 'FormsUI/actions/Inboxes';
import { showDisableTicketWarningDialog } from 'FormsUI/actions/DialogsAndPanels';
import { getCurrentSelectedLegalConsentOption } from 'FormsUI/selectors/LegalConsent';
import { getFormLanguage } from 'FormsUI/selectors/FormDefinition';
import { updateValueOnFormDefinition as updateValueOnFormDefinitionAction } from 'FormsUI/actions/FormDefinition';
import { getCustomObjects, searchQuerySelector, getCustomObjectsLoading, getFrequentlyUsedProperties, getSearchQueryResults, getShowSearchResults, requiredPropertiesSelector } from 'FormsUI/selectors/customObjects';
import { isCaptchaEnabled, isCaptchaConfigurable, hasRequiredTicketFields, getAllUsedFormFieldObjectTypeId } from 'FormsUI/selectors/FormDefinition';
import { hasConversationsAdminAccess, getHasFormsATCAccess } from 'FormsUI/selectors/Scope';
import { isFormLabelPropertyEnabled } from 'forms-utils-lib/settings/selectors';
import { getPaymentsToggleState } from 'FormsUI/selectors/Payments';
import NoResults from 'FormsUI/components/editor/leftPanel/common/NoResults';
import PropertiesTree from 'FormsUI/components/editor/leftPanel/properties/PropertiesTree';
import PropertiesGroup from 'FormsUI/components/editor/leftPanel/properties/PropertiesGroup';
import OtherFormElements from 'FormsUI/components/editor/leftPanel/properties/OtherFormElements';
import PaymentLink from 'FormsUI/components/editor/leftPanel/properties/Payment';
import CreateNewFieldHint from 'FormsUI/components/editor/leftPanel/properties/CreateNewFieldHint';
import FrequentlyUsedProperties from 'FormsUI/components/editor/leftPanel/properties/FrequentlyUsedProperties';
import { isTicketProperty } from 'FormsCommonComponents/utils/PropertyObjectTypes';
import TurnOnTicketsHint from 'FormsUI/components/editor/leftPanel/properties/TurnOnTicketsHint';
import { trackEditorInteraction } from 'FormsUI/utils/TrackingUtils';
import { ABM_CREATE_NEW_FIELD_HINT } from 'FormsUI/constants/TrackingInteractionKeys/Editor/FormTab';
import { hasAdConsentAccess as hasAdConsentAccessFromSelector } from 'FormsUI/selectors/AdConsentAccess';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ExistingPropertiesContainer = _ref => {
  let {
      customObjects = [],
      customObjectsLoading = false,
      frequentlyUsedProperties = [],
      hasPermissionsForTickets = false,
      searchQuery = '',
      searchQueryResults = [],
      ticketToggleEnabled = false,
      showSearchResults = false,
      captchaEnabled = false,
      captchaConfigurable = false,
      currentLegalConsentOption = '',
      usedFormFieldIds = {},
      isPaymentsToggleEnabled = false,
      hasAdConsentAccess = false,
      hasFormsATCAccess = false,
      setActiveTab = () => {},
      updateLegalConsentOption = () => {},
      updateValueOnFormDefinition = () => {},
      fetchInboxesAndSetInboxId = () => {}
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const handleCaptchaSelect = ({
    target: {
      checked = true
    }
  }) => {
    const options = {};
    if (checked) {
      options.scrollTarget = '#captcha-preview';
    }
    updateValueOnFormDefinition({
      captchaEnabled: checked
    }, options);
  };
  const handleCreateNewFieldHintClick = () => {
    trackEditorInteraction(ABM_CREATE_NEW_FIELD_HINT);
    setActiveTab();
  };
  const renderPropertiesObject = object => {
    const hasToggle = isTicketProperty(object.objectTypeId);
    const isTurnOnTicketsHintShown = hasToggle && hasPermissionsForTickets && !ticketToggleEnabled && hasFormsATCAccess;
    return /*#__PURE__*/_jsxs(PropertiesGroup, Object.assign({
      object: object,
      searchQuery: searchQuery,
      ticketToggleEnabled: ticketToggleEnabled,
      hasToggle: hasToggle,
      hasPermissionsForTickets: hasPermissionsForTickets,
      dataTourStep: `${object.label}`,
      defaultOpen: showSearchResults || false,
      fetchInboxesAndSetInboxId: fetchInboxesAndSetInboxId,
      isPaymentsToggleEnabled: isPaymentsToggleEnabled,
      hasFormsATCAccess: hasFormsATCAccess
    }, rest, {
      children: [isTurnOnTicketsHintShown && /*#__PURE__*/_jsx(TurnOnTicketsHint, {
        enableTickets: fetchInboxesAndSetInboxId,
        ticketToggleEnabled: ticketToggleEnabled
      }), object.propertyGroups && object.propertyGroups.map((group, idx) => /*#__PURE__*/_jsx(PropertiesTree, Object.assign({
        group: group,
        groupType: object.objectTypeLabel,
        dataId: group.name,
        isTicketsObject: hasToggle,
        defaultOpen: showSearchResults,
        usedFormFieldIds: usedFormFieldIds,
        ticketToggleEnabled: ticketToggleEnabled,
        disableProperties: hasToggle && (!ticketToggleEnabled || !hasFormsATCAccess)
      }, rest), `${group.name}-${idx}`))]
    }), `${object.objectTypeId}-${object.objectTypeLabel}`);
  };
  const maybeRenderSearchResults = () => {
    if (showSearchResults) {
      return searchQueryResults.length > 0 ? searchQueryResults.map(renderPropertiesObject) : /*#__PURE__*/_jsx(NoResults, {});
    }
    return /*#__PURE__*/_jsx("div", {
      "data-test-id": "property-objects",
      children: customObjects.map(renderPropertiesObject)
    });
  };
  return /*#__PURE__*/_jsx("div", {
    id: "forms_editor_leftPanel",
    children: !customObjectsLoading ? /*#__PURE__*/_jsxs(Fragment, {
      children: [customObjects.length > 0 ? /*#__PURE__*/_jsxs(Fragment, {
        children: [!showSearchResults && /*#__PURE__*/_jsx(FrequentlyUsedProperties, Object.assign({
          properties: frequentlyUsedProperties,
          usedFormFieldIds: usedFormFieldIds
        }, rest)), !showSearchResults && /*#__PURE__*/_jsx(OtherFormElements, {
          captchaEnabled: captchaEnabled,
          selectLegalConsent: updateLegalConsentOption,
          handleCaptchaSelect: handleCaptchaSelect,
          captchaConfigurable: captchaConfigurable,
          currentLegalConsentOption: currentLegalConsentOption,
          hasAdConsentAccess: hasAdConsentAccess
        }), !showSearchResults && /*#__PURE__*/_jsx(PaymentLink, {}), maybeRenderSearchResults()]
      }) : /*#__PURE__*/_jsx(NoResults, {}), /*#__PURE__*/_jsx(View, {
        padding: "800",
        as: "div",
        children: /*#__PURE__*/_jsx(View, {
          paddingBlockEnd: "1300",
          as: "div",
          children: /*#__PURE__*/_jsx(CreateNewFieldHint, {
            toggleToCreateTab: handleCreateNewFieldHintClick
          })
        })
      })]
    }) : /*#__PURE__*/_jsx(LoadingDots, {})
  });
};
const mapStateToProps = state => ({
  formLanguage: getFormLanguage(state),
  // default properties
  requiredProperties: requiredPropertiesSelector(state),
  frequentlyUsedProperties: getFrequentlyUsedProperties(state),
  // search
  searchQuery: searchQuerySelector(state),
  showSearchResults: getShowSearchResults(state),
  searchQueryResults: getSearchQueryResults(state),
  // All Properties Props
  customObjects: getCustomObjects(state),
  usedFormFieldIds: getAllUsedFormFieldObjectTypeId(state),
  customObjectsLoading: getCustomObjectsLoading(state),
  // other form elements props
  captchaEnabled: isCaptchaEnabled(state),
  captchaConfigurable: isCaptchaConfigurable(state),
  currentLegalConsentOption: getCurrentSelectedLegalConsentOption(state),
  // Ticket Only Props
  hasPermissionsForTickets: hasConversationsAdminAccess(state),
  isFormLabelPropertyEnabled: isFormLabelPropertyEnabled(state),
  ticketToggleEnabled: hasRequiredTicketFields(state),
  hasFormsATCAccess: getHasFormsATCAccess(state),
  // Payment Only Props
  isPaymentsToggleEnabled: getPaymentsToggleState(state),
  // Ad Consent props
  hasAdConsentAccess: hasAdConsentAccessFromSelector(state)
});
const mapActionsToProps = {
  updateDraggedItem,
  appendFormFieldGroup,
  // Ticket Only Props
  fetchInboxesAndSetInboxId: fetchInboxesAndSetInboxIdAction,
  showDisableTicketWarningDialog,
  updateLegalConsentOption: updateLegalConsentOptionFromAction,
  updateValueOnFormDefinition: updateValueOnFormDefinitionAction
};
export default connect(mapStateToProps, mapActionsToProps)(ExistingPropertiesContainer);